import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { closeDashboardDialog } from '@actions/dashboard-actions';
import { fetchLogsData } from '@actions/logs-actions';
import * as dialog from '@constants/dialogs';
import EntityTypeIcon from '@icons/entity-type-icon';
import Dialog from '@shared/dialogs/dialog';
import DialogSnackbar from '@shared/dialogs/dialog-snackbar';
import LogsBox from '@shared/dialogs/overlap-status-dialog/logs-box';
import ActionsBox from '@shared/dialogs/overlap-status-dialog/actions-box';
import StatusBox from '@shared/dialogs/overlap-status-dialog/status-box';
import TriptychWidget from '@shared/list-item/widgets/triptych-widget';
import { getStatus } from '@utils/data-detail/conflicts';
import {
  reachingTableEnd
} from '@utils/data-table-utils';
import { getConfigLabel, getConfigValue } from '@utils/list-item-utils';
import {
  allowResolution,
  getResolutionPermissions
} from '@utils/permission-utils';
import './overlap-status-dialog.scss';

const getMainEntityFromProps = (propsMainEntity, dialogParams) => {
  if (propsMainEntity) {
    return propsMainEntity;
  }
  if (dialogParams && dialogParams.mainEntity) {
    return dialogParams.mainEntity;
  }
  return null;
};

const OverlapStatusDialog = ({ mainEntity: propsMainEntity, opportunities }) => {
  const dispatch = useDispatch();

  const activeDialogs = useSelector(state => state.dashboard.activeDialogs);
  const dialogParams = useSelector(state => state.dashboard.dialogParams);
  const currentUser = useSelector(state => state.config.user);
  const loading = useSelector(state => state.logs.loading);
  const next = useSelector(state => state.logs.next);

  const { entity } = dialogParams[dialog.OVERLAP_STATUS] || {};
  const mainEntity = getMainEntityFromProps(propsMainEntity, dialogParams[dialog.OVERLAP_STATUS]);

  // Check if the entity.id exists in the list of opportunities of the main entity.
  const isOpportunity = useCallback(() => {
    if (!entity || !opportunities) {
      return false;
    }
    const { id } = entity;
    return typeof opportunities.find(({ id: opportunityId }) => opportunityId === id) !== 'undefined';
  }, [entity, opportunities]);

  const getOverlapStatus = useCallback(() => {
    if (!mainEntity || !entity) {
      return {};
    }
    const { overlap_status: mainOverlapStatus } = mainEntity;
    const { overlap_status } = entity;
    return getStatus(mainOverlapStatus, overlap_status, isOpportunity());
  }, [entity, isOpportunity, mainEntity]);

  const handleCancel = useCallback(() => {
    dispatch(closeDashboardDialog(dialog.OVERLAP_STATUS));
  }, [dispatch]);

  const getTitleName = useCallback(
    () => getConfigValue(entity.type_name, 'title', entity),
    [entity]
  );

  const getPriority = useCallback(
    categories => categories.find(category => category.name === 'priority'),
    []
  );

  const onScroll = useCallback(event => {
    if (reachingTableEnd(event) && !loading && next) {
      dispatch(fetchLogsData('overlap', 'conflict', mainEntity.id, next, { filters: 'comment_filters' }));
    }
  }, [dispatch, loading, mainEntity, next]);

  const renderTitle = () => (
    <div styleName="overlap-status-title-container">
      <div styleName="icon">
        <EntityTypeIcon type={entity.type_name} />
      </div>
      <div styleName="text">ID {entity.id}</div>
    </div>
  );

  const getEntityIcon = () => {
    const { icon } = entity;
    return (
      <div styleName="icon">
        {icon && <img src={icon} alt="" />}
      </div>
    );
  };

  const getTitle = () => {
    const label = getConfigLabel(entity.type_name, 'title');
    return (
      <div styleName="title-row">
        {label && <div styleName="label">{label}:</div>}
        {getEntityIcon()}
        <div styleName="title">{getTitleName()}</div>
      </div>
    );
  };

  const getDate = () => {
    const { category_dict, start_date, end_date } = entity;
    const priority = category_dict && getPriority(category_dict);
    return (
      <TriptychWidget
        displayDuration
        priority={priority ? priority.id : null}
        start={start_date}
        end={end_date}
      />
    );
  };

  const renderEntityCard = () => (
    <div styleName="overlap-status-entity-card-container">
      <div styleName="name">{entity.name}</div>
      {getTitle()}
      <div styleName="date-row">{getDate()}</div>
    </div>
  );

  const renderBody = () => {
    const { status } = getOverlapStatus();
    const isOpp = isOpportunity();
    const { overlap_status } = entity;
    const overlapStatus = overlap_status && overlap_status.length > 0 ? overlap_status[0] : null;
    return (
      <div styleName="overlap-status-body-container" onScroll={onScroll}>
        {renderEntityCard()}
        {allowResolution(entity.type_name, mainEntity.type_name) && (
          <StatusBox
            agency1={mainEntity.agency_name}
            agency2={entity.agency_name}
            overlapStatus={overlapStatus}
            status={status}
          />
        )}
        <ActionsBox
          entity={entity}
          isOpportunity={isOpp}
          mainEntity={mainEntity}
          permissions={getResolutionPermissions(entity, mainEntity, status, currentUser)}
        />
        <LogsBox
          app="overlap"
          id={entity.id}
          isOpportunity={isOpp}
          mainId={mainEntity.id}
          mainTypeName={mainEntity.type_name}
          model="conflict"
          typeName={entity.type_name}
        />
      </div>
    );
  };

  if (!entity || !mainEntity || !activeDialogs[dialog.OVERLAP_STATUS]) {
    return null;
  }

  return (
    <Dialog
      maxWidth="md"
      onClose={handleCancel}
      title={renderTitle()}
    >
      {renderBody()}
      <DialogSnackbar />
    </Dialog>
  );
};

OverlapStatusDialog.propTypes = {
  mainEntity: PropTypes.object,
  opportunities: PropTypes.array
};

export default OverlapStatusDialog;
